import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import api from '../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [businessData, setBusinessData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    category: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [businessSending, setBusinessSending] = useState(false);

  const handleBusinessSubmit = async (e) => {
    e.preventDefault();
    setBusinessSending(true);

    try {
      // For now, using the same endpoint or a placeholder
      await api.contact.submit({
        name: `${businessData.firstName} ${businessData.lastName}`,
        email: businessData.email,
        subject: `Business Inquiry: ${businessData.category}`,
        message: `Company: ${businessData.company}\nPhone: ${businessData.phone}\nCategory: ${businessData.category}\n\n${businessData.message}`
      });
      toast.success('Inquiry sent! We\'ll get back to you soon.');
      setBusinessData({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        phone: '',
        category: '',
        message: ''
      });
    } catch (error) {
      toast.error('Failed to send inquiry. Please try again.');
    } finally {
      setBusinessSending(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);

    try {
      await api.contact.submit(formData);
      toast.success('Message sent! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };



  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />

      <div className="py-24 px-6 md:px-12">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <h1 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6" data-testid="contact-heading">
              Get In <span className="gradient-text">Touch</span>
            </h1>
            <p className="text-lg text-slate-400 max-w-2xl mx-auto">
              Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">            <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="glass-card rounded-2xl p-8">
              <h2 className="text-2xl font-semibold mb-6">Talent Search Inquiry</h2>

              <form onSubmit={handleBusinessSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">First Name</label>
                    <input
                      type="text"
                      required
                      value={businessData.firstName}
                      onChange={(e) => setBusinessData({ ...businessData, firstName: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                      placeholder="First Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">Last Name</label>
                    <input
                      type="text"
                      required
                      value={businessData.lastName}
                      onChange={(e) => setBusinessData({ ...businessData, lastName: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                      placeholder="Last Name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Email</label>
                  <input
                    type="email"
                    required
                    value={businessData.email}
                    onChange={(e) => setBusinessData({ ...businessData, email: e.target.value })}
                    className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                    placeholder="your@email.com"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">Company</label>
                    <input
                      type="text"
                      required
                      value={businessData.company}
                      onChange={(e) => setBusinessData({ ...businessData, company: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                      placeholder="Company Name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2 text-slate-300">Phone</label>
                    <input
                      type="tel"
                      required
                      value={businessData.phone}
                      onChange={(e) => setBusinessData({ ...businessData, phone: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                      placeholder="Phone Number"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">Choose a category</label>
                  <Select
                    required
                    value={businessData.category}
                    onValueChange={(value) => setBusinessData({ ...businessData, category: value })}
                  >
                    <SelectTrigger className="w-full px-4 py-6 glass-input rounded-lg text-slate-200 bg-slate-800 border-none outline-none focus:ring-1 focus:ring-cyan-500/50">
                      <SelectValue placeholder="None" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-800 text-slate-200">
                      <SelectItem value="Staff Augmentation">Staff Augmentation</SelectItem>
                      <SelectItem value="Direct Placement">Direct Placement</SelectItem>
                      <SelectItem value="Web Development">Web Development</SelectItem>
                      <SelectItem value="Mobile App Development">Mobile App Development</SelectItem>
                      <SelectItem value="Social Media Marketing">Social Media Marketing</SelectItem>
                      <SelectItem value="Ad Campaign">Ad Campaign</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-slate-300">How can we help you?</label>
                  <textarea
                    required
                    value={businessData.message}
                    onChange={(e) => setBusinessData({ ...businessData, message: e.target.value })}
                    className="w-full px-4 py-3 glass-input rounded-lg text-slate-200 min-h-24"
                    placeholder="Write away here*"
                  />
                </div>

                <button
                  type="submit"
                  disabled={businessSending}
                  className="w-full btn-primary py-4 text-lg font-medium tracking-wide shadow-lg shadow-cyan-500/20 hover:shadow-cyan-500/40 transition-all duration-300"
                >
                  {businessSending ? 'Processing...' : 'Get started'}
                </button>
              </form>
            </div>
          </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="glass-card rounded-2xl p-8">
                <h2 className="text-2xl font-semibold mb-6">Opportunity Seeker Inquiry</h2>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                      placeholder="Your name"
                      data-testid="name-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                      placeholder="your@email.com"
                      data-testid="email-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Subject</label>
                    <input
                      type="text"
                      required
                      value={formData.subject}
                      onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200"
                      placeholder="What is this about?"
                      data-testid="subject-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Message</label>
                    <textarea
                      required
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="w-full px-4 py-3 glass-input rounded-lg text-slate-200 min-h-32"
                      placeholder="Your message..."
                      data-testid="message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={sending}
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    data-testid="send-message-btn"
                  >
                    <Send className="w-5 h-5" />
                    {sending ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;